import React from 'react';

import { useIntl } from 'react-intl';

import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import { L1_MENU_GROUPS } from '@atlassian/navigation-common';
import { isL1ItemData } from '@atlassian/navigation-drag-and-drop/dnd-data';
import {
	createElementRegistry,
	ElementRegistryProvider,
} from '@atlassian/navigation-drag-and-drop/element-registry';
import { GroupDropIndicatorWithDragOver } from '@atlassian/navigation-drag-and-drop/group-drop-indicator-with-drag-over';
import { useDragState } from '@atlassian/navigation-drag-and-drop/use-l1-drag-state';
import { NavErrorBoundary } from '@atlassian/navigation-error-boundary/ui';

import type { GlobalSideNavigationProps } from '../../common/types';
import NavItem from '../../common/ui/nav-item';
import {
	type ItemsAndShortcutsProviderProps,
	useItemsAndShortcuts,
} from '../../controllers/items-and-shortcuts-provider';
import { type OptionsProviderProps, useOptions } from '../../controllers/options-provider';
import { useDragAndDropSave } from '../../controllers/use-drag-and-drop-save';
import { useMonitorForDnDElements } from '../../controllers/use-monitor-for-dnd-elements';
import { useUserMenuDataShapes } from '../../controllers/user-state/user-menu-data-shapes-provider';
import { GLOBAL_SIDE_NAV_TEST_ID } from '../constants';
import { GSNSideNavigationLayout } from '../layout/gsn-layout';
import { PostMainItems } from '../post-main-items';
import { defaultErrorMessages, messages, mainNavItemMessages } from './messages';

export type SideNavProps = Omit<
	GlobalSideNavigationProps,
	keyof OptionsProviderProps | keyof ItemsAndShortcutsProviderProps | 'peekMenuId'
>;

export function SideNav(props: SideNavProps): React.JSX.Element {
	const {
		postItemsElem,
		postMainElem,
		openShortcutsInSameTab,
		viewAllAppsConfig,
		ShortcutsSlot,
		onPeekEnd,
		onPeekStart,
		onLeftNavCollapse,
		onLeftNavExpand,
		onResizeEnd,
		onResizeStart,
		sideNavContentRef,
	} = props;

	const { app, l1DragAndDropEnabled } = useOptions();
	const { allItems } = useItemsAndShortcuts();
	const userMenuDataShapes = useUserMenuDataShapes();
	const { formatMessage } = useIntl();

	// #region Drag and drop
	// Element registry for L1 items
	const [elementRegistry] = React.useState(createElementRegistry);
	const { dragAndDropSave } = useDragAndDropSave(userMenuDataShapes, elementRegistry);

	const [draggingL1Group, setDraggingL1Group] = React.useState<string | null>(null);

	useMonitorForDnDElements({
		l1DragAndDropEnabled,
		dragAndDropSave,
		setDraggingL1Group,
		hiddenMenuIds: userMenuDataShapes.hiddenMenuIds,
	});

	const { groupDropIndicatorRef, dragSource } = useDragState({
		dragAndDropEnabled: l1DragAndDropEnabled,
		isItemData: isL1ItemData,
	});
	// #endregion

	// #region Core rendering
	const mainNavItems = React.useMemo(
		() =>
			userMenuDataShapes.visibleItemMenuIds.map((id) => (
				<NavErrorBoundary
					key={id}
					errorMessages={mainNavItemMessages}
					errorMessageValues={{ menuId: id }}
					sentryConfig={{ product: app }}
					showFlags
				>
					<NavItem key={id} id={id} item={allItems[id]} />
				</NavErrorBoundary>
			)),
		[userMenuDataShapes.visibleItemMenuIds, app, allItems],
	);

	const topElem = React.useMemo(
		() =>
			props.topElem ? (
				<NavErrorBoundary
					errorMessages={defaultErrorMessages}
					sentryConfig={{ product: app }}
					showFlags
				>
					{props.topElem}
				</NavErrorBoundary>
			) : null,
		[app, props.topElem],
	);

	const bottomElem = React.useMemo(
		() =>
			props.bottomElem ? (
				<NavErrorBoundary
					errorMessages={defaultErrorMessages}
					sentryConfig={{ product: app }}
					showFlags
				>
					{props.bottomElem}
				</NavErrorBoundary>
			) : null,
		[app, props.bottomElem],
	);

	const preMainElem = React.useMemo(
		() =>
			props.preMainElem ? (
				<NavErrorBoundary
					errorMessages={defaultErrorMessages}
					sentryConfig={{ product: app }}
					showFlags
				>
					{props.preMainElem}
				</NavErrorBoundary>
			) : null,
		[app, props.preMainElem],
	);

	// The post-main element passed to GSNSideNavigationLayout. Like topElem /
	// preMainElem / bottomElem above, this is memoised so a justified SideNav
	// re-render does not hand the layout a fresh element identity every time
	// (which cascades a wasted re-render down the navigation tree — observed in
	// Chippy as `postMainElem: ref-changed` with identical content).
	const postMainElemForLayout = React.useMemo(
		() => (
			<NavErrorBoundary
				errorMessages={defaultErrorMessages}
				sentryConfig={{ product: app }}
				showFlags
			>
				<PostMainItems
					postItemsElem={postItemsElem}
					postMainElem={postMainElem}
					draggingL1Group={draggingL1Group}
					openShortcutsInSameTab={openShortcutsInSameTab}
					viewAllAppsConfig={viewAllAppsConfig}
					ShortcutsSlot={ShortcutsSlot}
				/>
			</NavErrorBoundary>
		),
		[
			app,
			postItemsElem,
			postMainElem,
			draggingL1Group,
			openShortcutsInSameTab,
			viewAllAppsConfig,
			ShortcutsSlot,
		],
	);

	return (
		<ElementRegistryProvider registry={elementRegistry}>
			<GSNSideNavigationLayout
				id={props.id}
				testId={props.testId ?? GLOBAL_SIDE_NAV_TEST_ID}
				preMainElem={topElem}
				onCollapse={onLeftNavCollapse}
				onExpand={onLeftNavExpand}
				onPeekEnd={onPeekEnd}
				onPeekStart={onPeekStart}
				onResizeEnd={onResizeEnd}
				onResizeStart={onResizeStart}
				sideNavContentRef={sideNavContentRef}
				postMainElem={postMainElemForLayout}
				bottomElem={bottomElem}
			>
				{mainNavItems?.length ? (
					<MenuList>
						{preMainElem}
						<GroupDropIndicatorWithDragOver
							ref={groupDropIndicatorRef}
							isActive={draggingL1Group === L1_MENU_GROUPS.MENU}
							ariaLabel={formatMessage(messages.primaryNavigationSectionScreenReader, {
								item: dragSource,
							})}
						>
							{mainNavItems}
						</GroupDropIndicatorWithDragOver>
					</MenuList>
				) : null}
			</GSNSideNavigationLayout>
		</ElementRegistryProvider>
	);
	// #endregion
}
